import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useRekognition, { RekognitionLabel } from '@/src/Activations/components/hooks/useRekognition';
import { useEventContext } from '@/src/v2/components/EventContext';
import EventPage from '../chrome/EventPage';
import { Crumb, Field, SaveBar, SectionCard, Select, TextInput, ToggleRow } from '../components/forms';
import { PageHeadActions } from '../components/nav';
import { useToast } from '../components/roster';
import { TCard, TEmpty, TSkeleton } from '../components/table';
import '../pages-event.css';

/*
 * Analyze — media analysis settings plus what it found, grouped by
 * label with thumbnails from the shared media cache. Labels feed the
 * gallery's public lookup pages.
 */

const DETECT_OPTIONS = [
    { value: 'number', label: 'Numbers (bibs, tags)' },
    { value: 'text', label: 'Text' },
    { value: 'face', label: 'Faces' },
];

const SHOWN = 8;

interface Draft {
    enabled: boolean;
    type: string;
    confidence: string;
}

const DEFAULTS: Draft = { enabled: false, type: 'number', confidence: '97' };

function AnalyzeBody() {
    const navigate = useNavigate();
    const { activationId: id, ensureMedia, media } = useEventContext();
    const rekognition = useRekognition();
    const toast = useToast();

    const [settingsId, setSettingsId] = useState<number | null>(null);
    const [saved, setSaved] = useState<Draft>(DEFAULTS);
    const [draft, setDraft] = useState<Draft>(DEFAULTS);
    const [labels, setLabels] = useState<RekognitionLabel[]>([]);
    const [labelsFailed, setLabelsFailed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!id) return;
        let mounted = true;
        ensureMedia();
        Promise.all([
            rekognition.getSettings(id).catch(() => null),
            rekognition.getLabels(id).catch((err) => (err?.response?.status === 404 ? { data: [] } : null)),
        ]).then(([settingsResponse, labelsResponse]) => {
            if (!mounted) return;
            const settings = settingsResponse?.data as any;
            if (settings?.id) {
                const loaded: Draft = {
                    enabled: !!settings.enabled,
                    type: settings.type ?? 'number',
                    confidence: String(Number(settings.confidence) || 97),
                };
                setSettingsId(settings.id);
                setSaved(loaded);
                setDraft(loaded);
            }
            setLabelsFailed(labelsResponse === null);
            setLabels(labelsResponse?.data ?? []);
            setIsLoading(false);
        });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, attempt]);

    const mediaById = useMemo(() => {
        const map = new Map<number, any>();
        (media ?? []).forEach((m: any) => map.set(m.id, m));
        return map;
    }, [media]);

    const groups = useMemo(() => {
        const byLabel = new Map<string, RekognitionLabel[]>();
        labels.forEach((row) => {
            const key = row.label ?? '—';
            byLabel.set(key, [...(byLabel.get(key) ?? []), row]);
        });
        return Array.from(byLabel.entries())
            .map(([label, items]) => ({ label, items }))
            .sort((a, b) => b.items.length - a.items.length);
    }, [labels]);

    const confidenceNum = Number(draft.confidence);
    const confidenceError =
        draft.confidence.trim() === '' || !Number.isFinite(confidenceNum) || confidenceNum < 1 || confidenceNum > 100
            ? 'Enter a number from 1 to 100.'
            : undefined;

    const dirty =
        draft.enabled !== saved.enabled || draft.type !== saved.type || draft.confidence !== saved.confidence
            ? ['Analysis']
            : [];

    const save = async () => {
        if (saving || confidenceError) return;
        setSaving(true);
        setSaveError(null);
        try {
            if (settingsId) {
                await rekognition.updateSettings(settingsId, draft.enabled, draft.type, confidenceNum);
            } else {
                const response = await rekognition.createSettings(id, draft.enabled, draft.type, confidenceNum);
                setSettingsId((response.data as any)?.id ?? null);
            }
            setSaved(draft);
            toast.show('Analysis settings saved.');
        } catch {
            setSaveError('Couldn’t save the settings — try again.');
        }
        setSaving(false);
    };

    const openMedia = () => navigate(`/activations/${id}/media`);

    const matchNote = `${groups.length.toLocaleString()} label${groups.length === 1 ? '' : 's'} · ${labels.length.toLocaleString()} match${labels.length === 1 ? '' : 'es'}`;

    return (
        <div className="ev-page">
            <Crumb label="Media" onClick={openMedia} />
            <PageHeadActions
                title="Analyze"
                sub={
                    isLoading
                        ? 'Loading…'
                        : saved.enabled
                          ? 'On — new uploads are analyzed automatically'
                          : 'Off — turn analysis on to label new uploads'
                }
            />

            {isLoading ? (
                <TCard>
                    <TSkeleton rows={3} />
                </TCard>
            ) : (
                <div className="stack">
                    <SectionCard title="Settings" note="labels feed the gallery’s lookup pages">
                        <ToggleRow
                            label="Analyze uploads"
                            sub="detect labels on each new photo as it uploads"
                            checked={draft.enabled}
                            disabled={saving}
                            onChange={(on) => setDraft((d) => ({ ...d, enabled: on }))}
                        />
                        <div className="ev-fields ev-gap">
                            <Field label="Detect">
                                <Select
                                    value={draft.type}
                                    disabled={saving}
                                    onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value }))}
                                >
                                    {DETECT_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </Select>
                            </Field>
                            <Field label="Minimum confidence (%)" error={confidenceError}>
                                <TextInput
                                    type="number"
                                    inputMode="numeric"
                                    min={1}
                                    max={100}
                                    style={{ maxWidth: 120 }}
                                    value={draft.confidence}
                                    disabled={saving}
                                    aria-invalid={confidenceError ? true : undefined}
                                    onChange={(e) => setDraft((d) => ({ ...d, confidence: e.target.value }))}
                                />
                            </Field>
                        </div>
                        {saveError && <p className="field-e">{saveError}</p>}
                    </SectionCard>

                    <SectionCard title="Detected" note={labelsFailed ? undefined : matchNote}>
                        {labelsFailed ? (
                            <TEmpty
                                message="Couldn’t load detected labels."
                                actionLabel="Retry"
                                onAction={() => {
                                    setIsLoading(true);
                                    setAttempt((a) => a + 1);
                                }}
                            />
                        ) : groups.length === 0 ? (
                            <p className="quiet-empty">
                                Nothing detected yet
                                {saved.enabled ? ' — labels appear as photos upload.' : ' — turn analysis on and save.'}
                            </p>
                        ) : (
                            groups.map((group) => {
                                const open = expanded === group.label;
                                const shown = open ? group.items : group.items.slice(0, SHOWN);
                                return (
                                    <div className="ev-group" key={group.label}>
                                        <div className="ev-group-h">
                                            <span className="ev-mono">{group.label}</span>
                                            <span className="n">
                                                {group.items.length} photo{group.items.length === 1 ? '' : 's'}
                                            </span>
                                            <span className="sp" />
                                            {group.items.length > SHOWN && (
                                                <button
                                                    type="button"
                                                    className="tb-btn ghost sm"
                                                    aria-expanded={open}
                                                    onClick={() => setExpanded(open ? null : group.label)}
                                                >
                                                    {open ? 'Show less' : `Show all ${group.items.length}`}
                                                </button>
                                            )}
                                        </div>
                                        <div className="ev-thumbs">
                                            {shown.map((row) => {
                                                const m = mediaById.get(row.mediaId);
                                                return (
                                                    <button
                                                        key={row.id}
                                                        type="button"
                                                        className="thumb"
                                                        title={`Media ${row.mediaId} — open Media`}
                                                        aria-label={`Media ${row.mediaId}, labelled ${group.label} — open Media`}
                                                        onClick={openMedia}
                                                    >
                                                        {m ? (
                                                            <img src={m.thumbnailUri ?? m.originalUri} alt="" loading="lazy" />
                                                        ) : (
                                                            `#${row.mediaId}`
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </SectionCard>
                </div>
            )}

            <SaveBar
                dirty={dirty}
                saving={saving}
                onSave={save}
                onDiscard={() => {
                    setDraft(saved);
                    setSaveError(null);
                }}
            />
            {toast.node}
        </div>
    );
}

export default function AnalyzeV3() {
    return (
        <EventPage section="media">
            <AnalyzeBody />
        </EventPage>
    );
}
